import * as R from 'ramda';
import {InvariantError} from 'ts-invariant';
import {pipe} from '@practicegenius/pg-util/src/FunctionUtils';
import {
  ApiClient,
  UnknownServerData,
} from '@practicegenius/http-clients-js/src/types';
import {
  dropNilOrEmptyValues,
  objectKeysToCamelCaseDeep,
  renameKey,
} from '@practicegenius/pg-util/src/ObjectUtils';
import {parseNullableDate} from '@practicegenius/pg-util/src/DateUtils';
import {objectifyEmptyArray} from '@practicegenius/pg-util/src/DataUtils';
import {camelizeZodSchema} from '@practicegenius/pg-util/src/ZodUtils';
import {z} from 'zod';
import {countryCodes} from '@practicegenius/countries/src/types';
import {SocialAuthIntegration} from '../integrations';
import {BaseUser, ContactStatus, UserType} from './BaseUser';
import {Practice} from '../practice';
import {isChild} from './birthdate';

const transformCommonTimestamps = R.evolve({
  createdAt: parseNullableDate,
  updatedAt: parseNullableDate,
});

// Global User Info - snake_case schema for parsing API responses
export const globalUserInfoSchema = z.object({
  id: z.number(),
  firstname: z.string(),
  middlename: z.string(),
  lastname: z.string(),
  username: z.string(),
  avatar_url: z.string(),
  has_password: z.boolean(),
  responsible_firstname: z.string().nullable(),
  responsible_lastname: z.string().nullable(),
  birthdate: z.string().transform(val => parseNullableDate(val) as Date),
  created_at: z.string().transform(val => parseNullableDate(val) as Date),
  last_login_at: z
    .string()
    .nullable()
    .transform(val => parseNullableDate(val) as Date),
  registration_completed_at: z
    .string()
    .transform(val => parseNullableDate(val) as Date),
});

const camelizedGlobalUserInfoSchema = camelizeZodSchema(globalUserInfoSchema);

export type GlobalUserInfo = z.infer<typeof camelizedGlobalUserInfoSchema>;

const transformIncomingGlobalUserProfile: (
  data: UnknownServerData,
) => GlobalUserInfo = pipe<GlobalUserInfo>(
  objectKeysToCamelCaseDeep,
  transformCommonTimestamps,
  R.evolve({
    birthdate: parseNullableDate,
    lastLoginAt: parseNullableDate,
    registrationCompletedAt: parseNullableDate,
  }),
);

export const globalUserAddressSchema = z.object({
  id: z.number(),
  global_user_id: z.number(),
  type: z.enum(['physical', 'shipping', 'billing']),
  address_line_1: z.string(),
  address_line_2: z.string().nullable().optional(),
  city: z.string(),
  state: z.string().optional(),
  zip: z.string(),
  country: z.enum(countryCodes).or(z.literal('')),
  notes: z.string().nullable(),
  created_at: z.string().transform(val => parseNullableDate(val) as Date),
  updated_at: z.string().transform(val => parseNullableDate(val) as Date),
});

const camelizedGlobalUserAddressSchema = camelizeZodSchema(
  globalUserAddressSchema,
);

export type GlobalUserAddress = z.infer<
  typeof camelizedGlobalUserAddressSchema
>;

const transformGlobalUserAddresses = pipe<GlobalUserAddress>(
  objectKeysToCamelCaseDeep,
  transformCommonTimestamps,
);

// Global User Contact - snake_case schema for parsing API responses
// Uses discriminated union to enforce type/subtype relationship
const baseContactFields = {
  id: z.number(),
  global_user_id: z.number(),
  contact: z.string(),
  verified: z.boolean(),
  verification_identifier: z.string(),
  created_at: z.string().transform(val => parseNullableDate(val) as Date),
};

export const globalUserContactSchema = z.discriminatedUnion('type', [
  z.object({
    ...baseContactFields,
    type: z.literal('email'),
    sub_type: z.enum(['primary', 'responsible']),
  }),
  z.object({
    ...baseContactFields,
    type: z.literal('phone'),
    sub_type: z.enum(['home', 'cell']),
  }),
]);

const camelizedGlobalUserContactSchema = camelizeZodSchema(
  globalUserContactSchema,
);

export type GlobalUserContact = z.infer<
  typeof camelizedGlobalUserContactSchema
>;

// Derive the discriminated union type from the schema
export type GlobalUserContactType = z.infer<typeof globalUserContactSchema>;

export const transformGlobalUserContact = pipe<GlobalUserContact>(
  objectKeysToCamelCaseDeep,
  transformCommonTimestamps,
);

export type GlobalUserPracticeRecord = BaseUser & {
  type: UserType;
  status: 'active' | 'deleted' | 'new' | 'lockedout' | 'unqualified';
  cardNumber: string | null;
  points: number;
  isSynced: boolean;
  joinedAt: Date;
  startedAt: Date | null;
  registrationCompletedAt: Date | null;
  responsibleCellphone: string | null;
  responsibleCellphoneStatus: ContactStatus;
  responsibleEmail: string | null;
  responsibleEmailStatus: ContactStatus;
  responsibleFirstname: string | null;
  responsibleLastname: string | null;
  responsibleHomephone: string | null;
  notificationCounts?: {
    messages: number;
    appointments: number;
  };
};

const transformGlobalUserPractice = pipe<Practice>(
  objectKeysToCamelCaseDeep,
  renameKey('address1', 'addressLine1'),
  renameKey('address2', 'addressLine2'),
);

const transformGlobalUserPracticeRecord = (
  rawPracticeRecord: UnknownServerData,
  rawPracticeNotificationCountsRecord?: UnknownServerData,
) => {
  return pipe<GlobalUserPracticeRecord>(
    rawPracticeNotificationCountsRecord
      ? R.assoc('notificationCounts', rawPracticeNotificationCountsRecord)
      : R.identity,
    objectKeysToCamelCaseDeep,
    renameKey('address1', 'addressLine1'),
    renameKey('address2', 'addressLine2'),
    R.evolve({
      joinedAt: parseNullableDate,
      startedAt: parseNullableDate,
      registrationCompletedAt: parseNullableDate,
    }),
  )(rawPracticeRecord);
};

type GlobalUserSocialAccount = {
  provider: SocialAuthIntegration;
  providerUserId: string;
  createdAt: Date;
};

const transformGlobalUserSocialAccount = pipe<GlobalUserSocialAccount>(
  objectKeysToCamelCaseDeep,
  transformCommonTimestamps,
);

type GlobalUserHubSettings = {
  theme_id?: number;
  hide_birthday_from_feed?: boolean;
  hide_anniversary_from_feed?: boolean;
};

const transformGlobalUserHubSettings = (
  data: UnknownServerData[],
): Partial<GlobalUserHubSettings> => {
  if (R.isEmpty(data)) return {};
  return R.reduce(
    (acc: {}, setting: UnknownServerData) => {
      if (setting.key === 'theme_id') return {...acc, theme_id: setting.value};
      return {...acc, [setting.key]: !!Number(setting.value)};
    },
    {},
    data,
  );
};

/**
 * Given a base field name and a data transform function, return a function that will transform
 *  the response payload into a map of item ids to items.
 *
 * The response payload must contain a mapping field with the same base field name.
 */
const transformIncludedDataWithMappingField =
  <T>(
    baseFieldName: string,
    dataTransform: (data: UnknownServerData) => T,
    dataIdField: keyof T,
  ) =>
  (responsePayload: UnknownServerData): Record<number, T[]> => {
    const itemsById = pipe<Record<number, T>>(
      R.path(['included', baseFieldName]),
      R.map(dataTransform),
      // @ts-expect-error TS lacks the expressivity needed to properly infer this
      R.indexBy(R.prop(dataIdField)),
    )(responsePayload);
    const mappingData = pipe<Record<number, number[]>>(
      R.path(['included', `${baseFieldName}.mapping`]),
      objectifyEmptyArray,
    )(responsePayload);
    return R.mapObjIndexed(itemIds => {
      return R.map(itemId => itemsById[itemId], itemIds);
    }, mappingData);
  };

/** A Global User's contacts will always contain either or both of primary and responsible emails,
 *   but not neither */
type GlobalUserEmails =
  | {
      primary?: GlobalUserContact;
      responsible?: GlobalUserContact;
    }
  | {
      primary: GlobalUserContact;
      responsible?: GlobalUserContact;
    }
  | {
      primary: GlobalUserContact;
      responsible: GlobalUserContact;
    };

const getGlobalUserEmailContacts = (
  contacts: GlobalUserContact[],
): GlobalUserEmails => {
  const primaryEmail = contacts.find(
    c => c.type === 'email' && c.subType === 'primary',
  );
  const responsibleEmail = contacts.find(
    c => c.type === 'email' && c.subType === 'responsible',
  );

  if (!primaryEmail && !responsibleEmail)
    throw new InvariantError(
      'Global user contacts must contain at least one or both of primary and responsible emails',
    );

  return dropNilOrEmptyValues({
    primary: primaryEmail,
    responsible: responsibleEmail,
  }) as GlobalUserEmails;
};

export type GlobalUserProfile = {
  user: GlobalUserInfo;
  practices: Practice[];
  contacts: GlobalUserContact[];
  practiceRecords: GlobalUserPracticeRecord[];
  parents: GlobalUserInfo[];
  children: GlobalUserInfo[];
  siblings: GlobalUserInfo[];
  parentContacts: GlobalUserContact[];
  /** Maps global user id of parent to their practices */
  parentPractices: Record<number, Practice[]>;
  /** Maps global user id of parent to their practice records */
  parentPracticeRecords: Record<number, GlobalUserPracticeRecord[]>;
  childContacts: GlobalUserContact[];
  childAddresses: GlobalUserAddress[];
  childSocialAccounts: GlobalUserSocialAccount[];
  /** Maps global user id of child to their practices */
  childPractices: Record<number, Practice[]>;
  /** Maps global user id of child to their practice records */
  childPracticeRecords: Record<number, GlobalUserPracticeRecord[]>;
  siblingContacts: GlobalUserContact[];
  /** Maps global user id of sibling to their practices */
  siblingPractices: Record<number, Practice[]>;
  /** Maps global user id of sibling to their practice records */
  siblingPracticeRecords: Record<number, GlobalUserPracticeRecord[]>;
  addresses: GlobalUserAddress[];
  socialAccounts: GlobalUserSocialAccount[];
  hubSettings: GlobalUserHubSettings;
  themeId: number; // TODO get this from Hub settings?
};

/** A map of include names to transform functions  */
const userProfileIncludeTransformSpec = {
  contacts: pipe<GlobalUserContact[]>(
    R.path(['included', 'contacts']),
    R.map(transformGlobalUserContact),
  ),
  practices: pipe<Practice[]>(
    R.path(['included', 'practices']),
    R.map(transformGlobalUserPractice),
  ),
  practiceRecords: (data: UnknownServerData) => {
    const practiceRecords = R.path(
      ['included', 'practice_records'],
      data,
    ) as UnknownServerData[];
    const notificationCounts = R.path(
      ['included', 'practice_records.notification_counts'],
      data,
    );
    const indexedNotificationCounts = R.indexBy(
      R.prop('user_id'),
      notificationCounts as Array<{user_id: number}>,
    ) as Record<number, UnknownServerData>;
    return practiceRecords.map(practiceRecord => {
      return transformGlobalUserPracticeRecord(
        practiceRecord,
        R.pick(
          ['messages', 'appointments'],
          indexedNotificationCounts[practiceRecord.id],
        ),
      );
    });
  },
  parents: pipe<GlobalUserInfo[]>(
    R.path(['included', 'parents']),
    R.map(transformIncomingGlobalUserProfile),
  ),
  children: pipe<GlobalUserInfo[]>(
    R.path(['included', 'children']),
    R.map(transformIncomingGlobalUserProfile),
  ),
  siblings: pipe<GlobalUserInfo[]>(
    R.path(['included', 'siblings']),
    R.map(transformIncomingGlobalUserProfile),
  ),
  parentContacts: pipe<GlobalUserContact[]>(
    R.path(['included', 'parents.contacts']),
    R.map(transformGlobalUserContact),
  ),
  parentPractices: transformIncludedDataWithMappingField(
    'parents.practices',
    transformGlobalUserPractice,
    'id',
  ),
  parentPracticeRecords: transformIncludedDataWithMappingField(
    'parents.practice_records',
    transformGlobalUserPracticeRecord,
    'id',
  ),
  childContacts: pipe<GlobalUserContact[]>(
    R.path(['included', 'children.contacts']),
    R.map(transformGlobalUserContact),
  ),
  childPractices: transformIncludedDataWithMappingField(
    'children.practices',
    transformGlobalUserPractice,
    'id',
  ),
  childPracticeRecords: transformIncludedDataWithMappingField(
    'children.practice_records',
    transformGlobalUserPracticeRecord,
    'id',
  ),
  childAddresses: pipe<GlobalUserAddress[]>(
    R.path(['included', 'children.addresses']),
    R.map(transformGlobalUserAddresses),
  ),
  childSocialAccounts: pipe<GlobalUserSocialAccount[]>(
    R.path(['included', 'children.social_accounts']),
    R.map(transformGlobalUserSocialAccount),
  ),
  siblingContacts: pipe<GlobalUserContact[]>(
    R.path(['included', 'siblings.contacts']),
    R.map(transformGlobalUserContact),
  ),
  siblingPractices: transformIncludedDataWithMappingField(
    'siblings.practices',
    transformGlobalUserPractice,
    'id',
  ),
  siblingPracticeRecords: transformIncludedDataWithMappingField(
    'siblings.practice_records',
    transformGlobalUserPracticeRecord,
    'id',
  ),
  addresses: pipe<GlobalUserAddress[]>(
    R.path(['included', 'addresses']),
    transformGlobalUserAddresses,
  ),
  socialAccounts: pipe<GlobalUserSocialAccount[]>(
    R.path(['included', 'social_accounts']),
    R.map(transformGlobalUserSocialAccount),
  ),
  hubSettings: pipe<Partial<GlobalUserHubSettings>>(
    R.path(['included', 'hub_settings']),
    transformGlobalUserHubSettings,
  ),
  themeId: pipe<number>(
    R.path(['included', 'hub_settings']),
    R.find(R.propEq('theme_id', 'key')),
    R.propOr('1', 'value'),
    Number,
  ),
};

export const makeGlobalUserProfileTransfomer = <
  TFields extends Array<keyof typeof userProfileIncludeTransformSpec>,
>(
  includes: TFields,
) => {
  const transformSpec = R.pick(includes, userProfileIncludeTransformSpec);
  return pipe<Pick<GlobalUserProfile, TFields[number] | 'user'>>(
    R.prop('data'),
    R.applySpec({
      user: pipe(R.prop('item'), transformIncomingGlobalUserProfile),
      ...transformSpec,
    }),
  );
};

const transformFullUserProfile = makeGlobalUserProfileTransfomer(
  // @ts-expect-error This is an intentional behavior of TS
  Object.keys(userProfileIncludeTransformSpec),
);

const profileIncludes = [
  'contacts',
  'practices',
  'practice_records',
  'practice_record.notification_counts',
  'parents',
  'children',
  'siblings',
  'parents.contacts',
  'parents.practices',
  'parents.practice_records',
  'children.contacts',
  'children.addresses',
  'children.social_accounts',
  'children.practices',
  'children.practice_records',
  'siblings.contacts',
  'siblings.practices',
  'siblings.practice_records',
  'addresses',
  'social_accounts',
  'hub_settings',
];

/** Given an ApiClient authorized to api-core as a GlobalUser, fetch the GlobalUserProfile */
export const fetchGlobalUserProfile = (authorizedApiClient: ApiClient) =>
  authorizedApiClient._unsafe
    .get('/global_users/profile', {
      params: {
        include: profileIncludes.join(','),
      },
    })
    .then(transformFullUserProfile);

/** Get the correct email contact for the user given their age */
export const getMainEmailContact = (
  profile: GlobalUserProfile,
): GlobalUserContact => {
  const emails = getGlobalUserEmailContacts(profile.contacts);

  const ageBasedContact = isChild(profile.user.birthdate)
    ? (emails.responsible ?? emails.primary)
    : emails.primary;

  // There are some corner cases where an adult user could have only a responsible email
  //   (example: a user shortly after they turn 18)
  // In that case we fall back to the weaker logic of returning the first of primary or responsible email
  if (!ageBasedContact) {
    const fallbackContact = emails.primary ?? emails.responsible;
    if (!fallbackContact) {
      throw new InvariantError(
        'Global user must have at least one email contact',
      );
    }
    return fallbackContact;
  }

  return ageBasedContact;
};

/** Get the correct email to display for the user given their age */
export const getUserDisplayEmail = (profile: GlobalUserProfile) => {
  const email = getMainEmailContact(profile);
  return email.contact;
};

export const getRedactedContactInfo = (contactInfo: GlobalUserContact) => {
  // Check if it's an email address
  if (contactInfo.type === 'email') {
    const [localPart, domain] = contactInfo.contact.split('@');
    if (localPart.length <= 2) {
      return contactInfo.contact; // Too short to redact meaningfully
    }
    return `${localPart[0]}${'*'.repeat(localPart.length - 2)}${
      localPart[localPart.length - 1]
    }@${domain}`;
  }

  // Assume it's a phone number - remove all non-digits first
  const digits = contactInfo.contact.replace(/\D/g, '');
  if (digits.length <= 2) {
    return contactInfo.contact; // Too short to redact meaningfully
  }
  return `${'*'.repeat(digits.length - 2)}${digits.slice(-2)}`;
};
